/**
 * Define functions sending requests to the backend.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import { baseURL } from '@/config/env'
import axios from 'axios'

const storageApiUrl = import.meta.env.VITE_STORAGE_API_URL

import { SortDirection } from '@/components/IampTable'
import { UploadFileInfo } from '@/components/jobui/UploadFileInfo'
import { logger, LogTag } from '@/config/logger'
import { FIELD_START_DATETIME } from '@/pages/TaskList/JobListDef'
import {
  setAimdTaskList,
  setAllowedCountries,
  setApiKeyList,
  setCompanyConsumption,
  setCompanyKpi,
  setInvoiceInfo,
  setJobList,
  setJobListSortDirection,
  setLastAimdTaskId,
  setLastJobId,
  setLastWebhookLogId,
  setPriceList,
  setServicesInfo,
  setUserInfo,
  setUserInfoComplete,
  setWebhookList,
  setWebhookLogs,
  store,
} from '@/store'
import { isDebugMockup } from '@/utils/TestUtil'
import {
  apiRequest,
  AsyncUtil,
  createTimeStampValue,
  DefaultInitialInvoiceListLength,
  JobDetail,
  TimeStampValue,
  UserInfo,
} from '@amplify3/duplo-back-end'

/**
 * Batch of API requests to fetch data regarding the consumption of the user
 * (user information, consumption, KPI and invoices).
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchConsumptionDataBatch = AsyncUtil.singleFlight(() => {
  return Promise.all([
    fetchUser(),
    fetchUserComplete(),
    fetchAimdTaskList(),
    fetchAllTaskList(),
    fetchCompanyConsumption(),
    fetchCompanyKpi(),
    fetchInvoice(),
    fetchPrice(),
  ])
})

/**
 * Batch of API requests at initial loading of the webapp.
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchInitialBatch = AsyncUtil.singleFlight(() => {
  return Promise.all([
    store.dispatch(setJobListSortDirection(SortDirection.DESCENDING)),
    fetchConsumptionDataBatch(),
    fetchApiKeyList(),
    fetchServicesInfo(),
    fetchWebhooks(),
    fetchAllowedCountries(),
  ])
})

/** Fetch the list of allowed countries and store it in the state. */
export const fetchAllowedCountries = async () => {
  const { data } = await axios.create().get('/allowedCountries.json')
  store.dispatch(setAllowedCountries(data ?? []))
}

/**
 * Fetch the user's API key list from the back-end and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchApiKeyList = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({ baseURL, url: '/app', forceRefresh })
    store.dispatch(setApiKeyList(createTimeStampValue(data?.app_clients)))
  } catch (err) {
    console.error('Error in fetchApiKeyList:', err)
    throw err
  }
}

/**
 * Delete an API key on the back-end.
 * @param apiClientId The client ID of the API key to delete.
 */
export const deleteApiKey = async (apiClientId: string) => {
  try {
    const data = await apiRequest({
      method: 'delete',
      baseURL,
      url: `/app/${apiClientId}`,
    })
    return data
  } catch (err) {
    console.error('Error in deleteApiKey:', err)
    throw err
  }
}

/**
 * Fetch the company's consumption data and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchCompanyConsumption = async (
  forceRefresh: boolean = false
) => {
  try {
    const data = await apiRequest({
      baseURL,
      url: '/company/consumption',
      forceRefresh,
    })
    store.dispatch(
      setCompanyConsumption(createTimeStampValue(data.consumption))
    )
  } catch (err) {
    console.error('Error in fetchCompanyConsumption:', err)
    throw err
  }
}

/**
 * Fetch the company's KPI data and update the store. Uses a mock-up
 * response when debug mock-up mode is enabled.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchCompanyKpi = async (forceRefresh: boolean = false) => {
  try {
    let data

    if (isDebugMockup()) {
      // load a mock-up of API response for debug and tests
      const mock = await import('@/test/company_kpi_mockup.json')
      data = mock.default
    } else {
      // Request KPI from the backend
      data = await apiRequest({ baseURL, url: '/company/kpis', forceRefresh })
    }

    if (isDebugMockup()) {
      // Warn the use of mock-up in the logs
      logger.warning(
        LogTag.Util,
        'Use test mockup for /company/kpis response:',
        data
      )
    }
    store.dispatch(setCompanyKpi(createTimeStampValue(data?.kpis)))
  } catch (err) {
    console.error('Error in fetchCompanyKpi:', err)
    throw err
  }
}

/**
 * Fetch invoices from the back-end and update the state apiInvoiceResponse.
 * @param previousId The ID of last fetched invoice to get the next ones.
 */
export async function fetchInvoice(
  previousId: string | null = null,
  forceRefresh: boolean = false
) {
  try {
    const params: Record<string, any> = {}
    params['limit'] = DefaultInitialInvoiceListLength
    if (previousId) {
      params['last_key'] = previousId
    }

    const data = await apiRequest({
      baseURL,
      url: '/user/invoices',
      params,
      // Pagination changes the request; only cache the first page.
      cacheKey: previousId ? false : undefined,
      forceRefresh,
    })
    store.dispatch(setInvoiceInfo(createTimeStampValue(data)))
  } catch (err) {
    console.error('Error in fetchInvoice:', err)
    throw err
  }
}

interface fetchTaskListProps {
  /** Current cached job list, used to append new results when paginating. */
  currentList?: TimeStampValue<JobDetail[]>
  /** Redux action to save the fetched job list to the store. */
  setList: any
  /** Redux action to save the last fetched job ID to the store. */
  setLastId: any
  /** ID of the last fetched job, used as a cursor for pagination. */
  lastId?: string
  /** If true, replaces the existing list; if false, appends to it. */
  refreshing?: boolean
  /** Filter jobs by service names. Sent as a JSON array to the backend. */
  serviceNameList?: string[]
  /** Sort order applied to the job list. */
  sortDirection?: SortDirection
  /** Authenticated user info, used to build the client_id query param.
   * If not given the current user information is used by default.*/
  userInfo?: TimeStampValue<UserInfo>
}

/**
 * Request the list of tasks or jobs from the back-end.
 */
const fetchTaskList = async (props: fetchTaskListProps) => {
  try {
    let user = props.userInfo
    if (!user) {
      const { userInfo } = store.getState().user
      user = userInfo
    }

    const sortDir = props.sortDirection ?? SortDirection.DESCENDING

    const lastJobParam =
      !props.refreshing && props.lastId?.length
        ? `&last_job_id=${props.lastId}`
        : ''

    const baseUrl =
      `/company/jobs?client_id=${user?.value.user_cognito_id}` +
      '&limit=10' +
      `&sort=["${FIELD_START_DATETIME}", "${sortDir}"]` +
      lastJobParam +
      '&reserved=with_cost_info'
    const url = props.serviceNameList
      ? baseUrl + `&service_name=${JSON.stringify(props.serviceNameList)}`
      : baseUrl

    // Paginated/filtered requests vary per call; don't cache them.
    const data = await apiRequest({
      baseURL,
      url,
      cacheKey: false,
      forceRefresh: true,
    })

    const jobsToSave = props.refreshing
      ? (data?.jobs ?? [])
      : [
          ...new Map(
            [...(props.currentList?.value ?? []), ...(data?.jobs ?? [])].map(
              (item) => [item?.job_id, item]
            )
          ).values(),
        ]

    const sortedJobs =
      props.sortDirection === SortDirection.ASCENDING
        ? jobsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.job_start_datetime ?? 0).getTime() -
              new Date(b?.job_start_datetime ?? 0).getTime()
          )
        : jobsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.job_start_datetime ?? 0).getTime() -
              new Date(a?.job_start_datetime ?? 0).getTime()
          )

    store.dispatch(props.setList(createTimeStampValue(sortedJobs)))
    store.dispatch(props.setLastId(data?.last_job_id))
  } catch (err) {
    console.error('Error in fetchTaskList:', err)
    throw err
  }
}

/**
 * Fetch the list of AI Music Detector tasks and update the store.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchAimdTaskList = async ({
  refreshing = undefined,
}: {
  refreshing?: boolean
} = {}) => {
  const { aimdTaskList, lastAimdTaskId, userInfo, aimdTaskListSortDirection } =
    store.getState().user

  await fetchTaskList({
    currentList: aimdTaskList,
    lastId: lastAimdTaskId,
    refreshing: refreshing,
    serviceNameList: ['aidetector'],
    setList: setAimdTaskList,
    setLastId: setLastAimdTaskId,
    sortDirection: aimdTaskListSortDirection,
    userInfo: userInfo,
  })
}

/**
 * Fetch the list of all jobs/tasks and update the store.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchAllTaskList = async ({
  refreshing = undefined,
}: {
  refreshing?: boolean
} = {}) => {
  const { jobList, lastJobId, userInfo, jobListSortDirection } =
    store.getState().user

  await fetchTaskList({
    currentList: jobList,
    lastId: lastJobId,
    refreshing: refreshing,
    setList: setJobList,
    setLastId: setLastJobId,
    sortDirection: jobListSortDirection,
    userInfo: userInfo,
  })
}

/**
 * Fetch the checkout price list and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchPrice = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({
      baseURL,
      url: '/checkout/prices',
      forceRefresh,
    })
    store.dispatch(setPriceList(createTimeStampValue(data?.prices)))
  } catch (err) {
    console.error('Error in fetchPrice:', err)
    throw err
  }
}

/**
 * Fetch information about available services and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchServicesInfo = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({
      baseURL,
      url: '/services-info',
      forceRefresh,
    })
    store.dispatch(setServicesInfo(createTimeStampValue(data)))
  } catch (err) {
    console.error('Error in fetchServicesInfo:', err)
    throw err
  }
}

/**
 * Fetch the current user's information and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchUser = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({ baseURL, url: '/user', forceRefresh })
    store.dispatch(setUserInfo(createTimeStampValue(data?.user)))
  } catch (err) {
    console.error('Error in fetchUser:', err)
    throw err
  }
}

/**
 * Fetch the current user's complete information (including extra fields)
 * and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchUserComplete = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({
      baseURL,
      url: '/user',
      params: { all: true },
      // Same URL as fetchUser but different params; needs its own key
      // since the default cache key is derived from the URL alone.
      cacheKey: 'userInfoComplete',
      forceRefresh,
    })
    store.dispatch(setUserInfoComplete(createTimeStampValue(data)))
  } catch (err) {
    console.error('Error in fetchUserComplete:', err)
    throw err
  }
}

/**
 * Request the sending of an invitation to create a sub-user.
 * @param company_id The company ID which will send the invitation.
 * @param email The email of the recipient.
 */
export async function inviteSubUser(company_id: string, email: string) {
  try {
    const data = await apiRequest({
      method: 'post',
      baseURL,
      url: '/company/invite-user',
      data: { email, company_id },
    })
    return data
  } catch (err) {
    console.error('Error in inviteSubUser:', err)
    throw err
  }
}

/**
 * Create a webhook on the back-end.
 * @param name The webhook's display name.
 * @param url The URL the webhook will call.
 * @param api_key_id The API key to associate the webhook with, if any.
 * @param secret The signing secret to associate with the webhook, if any.
 */
export const requestCreateWebhook = async (
  name: string,
  url: string,
  api_key_id?: string,
  secret?: string
) => {
  try {
    const param: Record<string, string> = { name, url }

    if (secret && secret?.length > 0) {
      param['secret'] = secret
    }

    if (api_key_id && api_key_id?.length > 0) {
      param['client_id'] = api_key_id
    }

    const data = await apiRequest({
      method: 'post',
      baseURL,
      url: '/webhook',
      data: param,
    })
    logger.debug(`createWebhook: name=${name} id=${data.webhook_id}`)
    return data
  } catch (err) {
    console.error('Error in createWebhook:', err)
    throw err
  }
}

/**
 * Fetch the user's webhook list from the back-end and update the store.
 * @param forceRefresh If true, bypasses the cache and refetches
 *   unconditionally.
 */
export const fetchWebhooks = async (forceRefresh: boolean = false) => {
  try {
    const data = await apiRequest({ baseURL, url: '/webhook', forceRefresh })
    store.dispatch(setWebhookList(createTimeStampValue(data?.webhooks)))
  } catch (err) {
    console.error('Error in fetchWebhooks:', err)
    throw err
  }
}

/**
 * Delete a webhook on the back-end.
 * @param webhookId The ID of the webhook to delete.
 */
export const deleteWebhook = async (webhookId: string) => {
  try {
    const data = await apiRequest({
      method: 'delete',
      baseURL,
      url: `/webhook/${webhookId}`,
    })
    return data
  } catch (err) {
    console.error('Error in deleteWebhook:', err)
    throw err
  }
}

/**
 * Fetch the logs of a webhook from the back-end and update the store.
 * @param webhookId The ID of the webhook whose logs are fetched.
 * @param refreshing If true, replaces the existing list; if false, appends.
 */
export const fetchWebhookLogs = async ({
  webhookId,
  refreshing,
  forceRefresh,
}: {
  webhookId: string
  refreshing: boolean
  forceRefresh?: boolean
}) => {
  try {
    const { webhookLogList, webhookLogListSortDirection, lastWebhookLogId } =
      store.getState().user

    const sortDir = webhookLogListSortDirection ?? SortDirection.ASCENDING
    const lastLogParam =
      !refreshing &&
      lastWebhookLogId !== undefined &&
      lastWebhookLogId?.length > 0
        ? `&last_log_id=${lastWebhookLogId}`
        : ''
    const url =
      `webhook/${webhookId}/log?limit=10` +
      `&sort=["date", "${sortDir}"]` +
      lastLogParam
    // Paginated/sorted requests vary per call; don't cache them.
    const data = await apiRequest({
      baseURL,
      url,
      cacheKey: false,
      forceRefresh,
    })

    const logsToSave = refreshing
      ? (data?.webhook_logs ?? [])
      : [
          ...new Map(
            [...(webhookLogList ?? []), ...(data?.webhook_logs ?? [])].map(
              (item) => [item?.id, item]
            )
          ).values(),
        ]
    const sortedLogs =
      webhookLogListSortDirection === SortDirection.ASCENDING
        ? logsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.date ?? 0).getTime() -
              new Date(b?.date ?? 0).getTime()
          )
        : logsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.date ?? 0).getTime() -
              new Date(a?.date ?? 0).getTime()
          )

    store.dispatch(setWebhookLogs(sortedLogs))
    store.dispatch(setLastWebhookLogId(data?.last_log_id))
  } catch (err) {
    console.error('Error in fetchWebhookLogs:', err)
    throw err
  }
}

/**
 * Request the processing of a Duplo module.
 * @param payload The payload of the API request.
 * @param api The name of the API.
 * @returns The API response of the request.
 */
export const requestJobProcessing = async (
  payload: string,
  api: string,
  forceRefresh?: boolean
) => {
  return apiRequest({
    method: 'post',
    baseURL,
    url: `/${api}`,
    data: payload,
    headers: { 'Content-Type': 'application/json' },
    forceRefresh,
  })
}

/**
 * Fetch the report of a job/task, using the current auth session.
 * @param jobId The ID of the job.
 * @param api The name of the API/module the job was processed by.
 */
export const getJobReportFromSession = async (
  jobId: string,
  api: string,
  forceRefresh?: boolean
) => {
  if (api.endsWith('/')) {
    api = api.substring(0, api.length - 1)
  }

  return apiRequest({
    baseURL,
    url: `/${api}/${jobId}`,
    headers: { 'Content-Type': 'application/json' },
    forceRefresh,
  })
}

/**
 * Calculate the cost of processing a job before submitting it.
 * @param iasPathsArray The list of IAS paths of the input files.
 * @param serviceName The name of the service/module.
 */
export const calculateCost = async (
  iasPathsArray: string[],
  serviceName: string
) => {
  const payload = {
    ias_paths: iasPathsArray,
    service_name: serviceName,
  }
  logger.debug('calculateCost Payload:', payload)
  const resp = await apiRequest({
    method: 'post',
    baseURL,
    url: '/job/calculate-cost',
    data: payload,
    headers: { 'Content-Type': 'application/json' },
  })
  logger.debug('calculateCost Response:', resp)
  return resp
}

/** Create a Cognito-authenticated storage session for file uploads. */
export const createStorageCognito = async () => {
  return apiRequest({
    method: 'post',
    baseURL: storageApiUrl,
    url: 'manager/',
    data: {},
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {})
}

/**
 * Upload a file to the storage service, using a Cognito-authenticated
 * session.
 * @param file The file to upload.
 * @param id The storage session ID to upload to.
 */
export const uploadFileToIasCognito = async (
  file: UploadFileInfo,
  id: string
) => {
  // Check if localFile is defined
  if (!file.localFile) {
    throw new Error(
      'Local file is undefined. Please provide a valid file to upload.'
    )
  }

  return apiRequest({
    method: 'put',
    baseURL: storageApiUrl,
    url: `${id}/${file.localFile.name}`,
    data: file.localFile,
    headers: { 'Content-Type': 'application/octet-stream' },
  })
}
